import React from 'react';
import { UserIcon, EditIcon, PhoneIcon, MailIcon, CrownIcon, WalletIcon, StarIcon, PackageIcon } from 'lucide-react';
interface UserProfileCardProps {
  user?: {
    name: string;
    email: string;
    phone: string;
    avatar?: string;
  };
  onSignIn: () => void;
  onRegister: () => void;
  onEdit?: () => void;
  /** Quick stats shown as glassy chips on the gradient header (mobile) */
  stats?: {
    points?: number;
    walletBalance?: string;
    orders?: number;
  };
  /** Tier label shown next to name (e.g. "Gold Member") */
  tier?: string;
}
export function UserProfileCard({
  user,
  onSignIn,
  onRegister,
  onEdit,
  stats,
  tier
}: UserProfileCardProps) {
  // Guest view (unchanged simple layout)
  if (!user) {
    return <div className="bg-white px-4 py-6 border-b border-gray-200" data-id="element-1173" data-mp="2o33ef8k7siah">
        <div className="flex items-center gap-4" data-id="element-1174" data-mp="xtjqdxd1qy91">
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-1175" data-mp="tr6m7ddy78ed">
            <UserIcon className="w-8 h-8 text-gray-400" data-id="element-1176" data-mp="2ouulw1qene5y" />
          </div>
          <div className="flex-1" data-id="element-1177" data-mp="2osqa6xm9lav4">
            <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1178" data-mp="2tsw7ouf7trl4">
              Hello, Guest
            </h2>
            <div className="flex gap-3" data-id="element-1179" data-mp="2yrpn4se2bsnx">
              <button onClick={onSignIn} className="px-6 py-2 bg-[#1E2B55] text-white text-sm font-semibold rounded-md hover:bg-[#162041] transition-colors" data-id="element-1180" data-mp="2yqnzzpjrvfpu">
                Sign In
              </button>
              <button onClick={onRegister} className="px-6 py-2 border-2 border-[#1E2B55] text-[#1E2B55] text-sm font-semibold rounded-md hover:bg-gray-50 transition-colors" data-id="element-1181" data-mp="3cjdxaq4r4729">
                Register
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  const tierLabel = tier ?? 'Gold Member';
  return <div className="relative bg-white" data-id="element-1182" data-mp="1s3sgzju5xl02">
      {/* Gradient hero (mobile-tuned, app-like) */}
      <div className="relative bg-gradient-to-br from-[#1E2B55] via-[#243567] to-[#2a3d6f] px-4 pt-5 pb-16 overflow-hidden" data-id="element-1183" data-mp="1k32x7bdrav4p">
        {/* Decorative glow */}
        <div className="absolute -top-12 -right-10 w-48 h-48 rounded-full bg-white/5 blur-2xl pointer-events-none" aria-hidden="true" data-id="element-1184" data-mp="1g3u6yghxdfdw" />
        <div className="absolute -bottom-10 -left-6 w-40 h-40 rounded-full bg-amber-400/10 blur-2xl pointer-events-none" aria-hidden="true" data-id="element-1185" data-mp="1p1nx19p3jcsm" />
        <div className="relative flex items-center gap-3" data-id="element-1186" data-mp="1z150rlksfgly">
          {/* Avatar with gold tier ring */}
          <div className="relative flex-shrink-0" data-id="element-1187" data-mp="2cstd9pyy873g">
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-amber-300 to-amber-500 p-[2px]" data-id="element-1188" data-mp="2cri8pnxoivba">
              <div className="w-full h-full rounded-full bg-[#1E2B55]" data-id="element-1189" data-mp="27vjfnn1eop4j" />
            </div>
            <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-[#2a3d6f] to-[#1E2B55] flex items-center justify-center m-[2px]" data-id="element-1190" data-mp="2mod786tycvpk">
              {user.avatar ? <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" data-id="element-1191" data-mp="31doarybvz69f" /> : <span className="text-2xl font-bold text-white" data-id="element-1192" data-mp="2regy7dcz7fnd">
                  {user.name.charAt(0).toUpperCase()}
                </span>}
            </div>
            {/* Tier crown badge */}
            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center ring-2 ring-[#1E2B55]" data-id="element-1193" data-mp="2hnw4a388yzas">
              <CrownIcon className="w-3.5 h-3.5 text-[#1E2B55]" data-id="element-1194" data-mp="28pskpcgtoclm" />
            </div>
          </div>
          <div className="flex-1 min-w-0" data-id="element-1195" data-mp="2dodg8yl4q83q">
            <h2 className="text-lg font-bold text-white truncate" data-id="element-1196" data-mp="1yqbdgqlx9qnj">
              {user.name}
            </h2>
            <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-400/20 border border-amber-300/40" data-id="element-1197" data-mp="23p4rck54fjo8">
              <StarIcon className="w-3 h-3 text-amber-300 fill-amber-300" data-id="element-1198" data-mp="36z0htn449kp2" />
              <span className="text-[11px] font-semibold text-amber-100" data-id="element-1199" data-mp="320papk57hoaj">
                {tierLabel}
              </span>
            </div>
          </div>
          <button onClick={onEdit} aria-label="Edit profile" className="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-white flex items-center justify-center active:scale-95 hover:bg-white/20 transition" data-id="element-1200" data-mp="2x8fjm0scui6o">
            <EditIcon className="w-4.5 h-4.5" data-id="element-1201" data-mp="2jcu18cgmmwn8" />
          </button>
        </div>
        {/* Contact line — compact */}
        <div className="relative mt-3 flex items-center gap-3 text-[12px] text-white/75" data-id="element-1202" data-mp="29cjnmqqojn7j">
          <span className="inline-flex items-center gap-1.5 truncate" data-id="element-1203" data-mp="29ewdrr6ld3vo">
            <MailIcon className="w-3.5 h-3.5" data-id="element-1204" data-mp="cji4nasa7ga2" />
            <span className="truncate" data-id="element-1205" data-mp="7lgcm0cvo6mg">{user.email}</span>
          </span>
          <span className="w-1 h-1 rounded-full bg-white/40 flex-shrink-0" data-id="element-1206" data-mp="7jdnrjngojjb" />
          <span className="inline-flex items-center gap-1.5 flex-shrink-0" data-id="element-1207" data-mp="f4fsvx51li1l">
            <PhoneIcon className="w-3.5 h-3.5" data-id="element-1208" data-mp="k2hmvd40b1ru" />
            <span data-id="element-1209" data-mp="ouiuumu566c2">{user.phone}</span>
          </span>
        </div>
      </div>
      {/* Floating stats card overlapping gradient */}
      <div className="px-4 -mt-10 relative z-10" data-id="element-1210" data-mp="ttvg535d75cj">
        <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(30,43,85,0.12)] border border-gray-100 grid grid-cols-3 divide-x divide-gray-100" data-id="element-1211" data-mp="tvon6jjw3rl2">
          <StatCell icon={<StarIcon className="w-4 h-4 text-amber-500" data-id="element-1213" data-mp="15gd7ezicdhk" />} label="Points" value={(stats?.points ?? 3250).toLocaleString()} data-id="element-1212" data-mp="1453kyjdsbij" />
          <StatCell icon={<WalletIcon className="w-4 h-4 text-emerald-600" data-id="element-1215" data-mp="b3vukq8qg8dv" />} label="Wallet" value={stats?.walletBalance ?? 'SAR 250'} data-id="element-1214" data-mp="b3nk15031f5u" />
          <StatCell icon={<PackageIcon className="w-4 h-4 text-[#1E2B55]" data-id="element-1217" data-mp="1mh83vgn9hj7f" />} label="Orders" value={String(stats?.orders ?? 5)} data-id="element-1216" data-mp="1mi119r0bywcw" />
        </div>
      </div>
    </div>;
}
function StatCell({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex flex-col items-center justify-center py-3 px-2" data-id="element-1218" data-mp="3rksvnwvtiqi3">
      <div className="flex items-center gap-1 mb-0.5" data-id="element-1219" data-mp="3njza3a5ulnsd">{icon}</div>
      <span className="text-[11px] text-gray-500 leading-none mt-0.5" data-id="element-1220" data-mp="3nio5j84kwc07">
        {label}
      </span>
      <span className="text-sm font-bold text-gray-900 mt-1 leading-none" data-id="element-1221" data-mp="2zsjzku3x4061">
        {value}
      </span>
    </div>;
}